import { FC, useCallback, useMemo } from "react"
import {
  CustomFieldDefinition,
  CustomFieldValue
} from "../../../types/pricing-engine"
import { useGetCustomFieldsQuery } from "../../../redux/queries/pricing-engine"
import Select from "../form/select"
import { ListSkeleton } from "../skeletons"
import { AlertCircle } from "lucide-react"

interface Props {
  rateflowId: string
  currentCustomFields: CustomFieldValue[]
  onChange: (newCustomFields: CustomFieldValue[]) => void
}

const CustomFieldsManager: FC<Props> = ({
  rateflowId,
  currentCustomFields,
  onChange
}) => {
  const {
    data: customFields,
    isFetching,
    error
  } = useGetCustomFieldsQuery(
    { rateflow_id: rateflowId },
    { skip: !rateflowId }
  )

  // Build a map of current values for easy lookup
  const currentValuesMap = useMemo(() => {
    const map: Record<string, string | number> = {}
    currentCustomFields.forEach(cf => {
      map[cf.customFieldInputName] = cf.customFieldValue
    })
    return map
  }, [currentCustomFields])

  const handleValueChange = useCallback(
    (fieldName: string, value: string | number) => {
      // Update the custom fields array
      const existingIndex = currentCustomFields.findIndex(
        cf => cf.customFieldInputName === fieldName
      )

      let newCustomFields: CustomFieldValue[]
      if (existingIndex >= 0) {
        // Update existing
        newCustomFields = currentCustomFields.map((cf, i) =>
          i === existingIndex ? { ...cf, customFieldValue: value } : cf
        )
      } else {
        // Add new
        newCustomFields = [
          ...currentCustomFields,
          { customFieldInputName: fieldName, customFieldValue: value }
        ]
      }

      onChange(newCustomFields)
    },
    [currentCustomFields, onChange]
  )

  if (isFetching) {
    return <ListSkeleton />
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg">
        <AlertCircle size={20} />
        <span>Failed to load custom fields. Please try again later.</span>
      </div>
    )
  }

  if (!customFields || Object.keys(customFields).length === 0) {
    return (
      <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
        No custom fields configured for this pricing engine.
      </div>
    )
  }

  const fieldEntries = Object.entries(customFields)

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600 mb-4">
          Configure default values for custom fields. These values will be used
          when pricing loans unless overridden.
        </p>

        <div className="space-y-4">
          {fieldEntries.map(([fieldKey, field]) => (
            <CustomFieldInput
              key={fieldKey}
              field={field}
              value={currentValuesMap[field.customFieldInputName]}
              onChange={value =>
                handleValueChange(field.customFieldInputName, value)
              }
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface CustomFieldInputProps {
  field: CustomFieldDefinition
  value: string | number | undefined
  onChange: (value: string | number) => void
}

const CustomFieldInput: FC<CustomFieldInputProps> = ({
  field,
  value,
  onChange
}) => {
  const label = `${field.customFieldDescription}${field.isRequired ? " *" : ""}`
  const inputClassName =
    "block w-full px-3 py-2.5 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

  if (field.typeOfField === "List" && field.customListValues) {
    const options = field.customListValues
      .filter(lv => lv.displayInList)
      .map(lv => ({
        id: String(lv.customFieldValue),
        name: lv.customFieldListDescription
      }))

    return (
      <Select
        label={label}
        name={field.customFieldInputName}
        data={options}
        selected={value !== undefined ? String(value) : ""}
        setSelected={(val: string) => onChange(Number(val) || val)}
      />
    )
  }

  if (field.typeOfField === "Number") {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <input
          type="number"
          name={field.customFieldInputName}
          value={value !== undefined ? String(value) : ""}
          onChange={e => onChange(Number(e.target.value))}
          className={inputClassName}
        />
      </div>
    )
  }

  // Default to text input
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type="text"
        name={field.customFieldInputName}
        value={value !== undefined ? String(value) : ""}
        onChange={e => onChange(e.target.value)}
        className={inputClassName}
      />
    </div>
  )
}

export default CustomFieldsManager
